<template>
  <div
    :class="[
      'retailer-search-result-item-fi',
      theme ? `retailer-search-result-item-fi--${theme}` : null,
    ]"
    data-test="retailer-search-item"
  >
    <div class="retailer-search-result-item-fi__top">
      <div class="retailer-search-result-item-fi__title">
        <div
          v-if="retailer.Names[0]"
          class="retailer-search-result-item-fi__name"
        >
          {{ retailer.Names[0] }}
        </div>
        <div
          v-if="retailer.Names[1]"
          class="retailer-search-result-item-fi__subname"
        >
          {{ retailer.Names[1] }}
        </div>
      </div>
      <div
        class="retailer-search-result-item-fi__action"
        @click="emit('clicked', data)"
      >
        <span class="retailer-search-result-item-fi__action__text">{{
          $dict.get('dealer-Locator.Show-Retailer-Map')
        }}</span>
        <icon-fi
          icon="map-pin"
          class="retailer-search-result-item-fi__action__icon"
        />
      </div>
    </div>

    <div
      v-if="retailer.RetailerTypeList?.length"
      class="retailer-search-result-item-fi__retailer-type"
    >
      {{ retailer.RetailerTypeList.join(', ') }}
    </div>

    <div class="retailer-search-result-item-fi__bottom">
      <component
        :is="isSM ? 'div' : SlideUpDownFi"
        :model-value="showMore"
        :duration="700"
        class="retailer-search-result-item-fi__more-info"
      >
        <div class="retailer-search-result-item-fi__more-info__content">
          <div class="retailer-search-result-item-fi__address">
            <icon-fi
              class="retailer-search-result-item-fi__address__icon"
              icon="store"
            />
            <div class="retailer-search-result-item-fi__address__container">
              <div class="retailer-search-result-item-fi__address__details">
                <div
                  v-if="retailer.Address?.AddressLines?.[0]"
                  class="retailer-search-result-item-fi__address__detail"
                >
                  {{ retailer.Address.AddressLines[0] }}
                </div>
                <div
                  v-if="retailer.Address?.PostalCode"
                  class="retailer-search-result-item-fi__address__detail"
                >
                  {{ retailer.Address.PostalCode }} {{ retailer.Address.City }}
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="retailer.Contact"
            class="retailer-search-result-item-fi__contact"
          >
            <contact-detail-fi
              v-if="retailer.Contact.Phone"
              class="retailer-search-result-item-fi__contact__detail"
              icon="phone-call"
              :href="getTel(retailer.Contact.Phone)"
              :detail="retailer.Contact.Phone"
            />
            <contact-detail-fi
              v-if="retailer.Contact.Fax"
              class="retailer-search-result-item-fi__contact__detail"
              icon="printer"
              :href="getFax(retailer.Contact.Fax)"
              :detail="retailer.Contact.Fax"
            />
            <contact-detail-fi
              v-if="retailer.Contact.Email"
              class="retailer-search-result-item-fi__contact__detail"
              icon="mail"
              :href="getMailto(retailer.Contact.Email)"
              :detail="retailer.Contact.Email"
            />
            <contact-detail-fi
              v-if="retailer.Contact.Url"
              class="retailer-search-result-item-fi__contact__detail"
              icon="globe"
              :detail="retailer.Contact.Url"
              :href="retailer.Contact.Url"
              target="_blank"
            />
          </div>

          <div
            v-if="retailer.PartnerType && partnerTypeLogo"
            class="retailer-search-result-item-fi__partner__container"
          >
            <div class="retailer-search-result-item-fi__partner">
              <cloud-image-fi
                :field="partnerTypeLogo"
                class="retailer-search-result-item-fi__partner__img"
              />
            </div>
          </div>
        </div>
      </component>
    </div>

    <availability-indicator
      v-if="showStock"
      class="retailer-search-result-item-fi__stock"
      :is-stocked="data.isStocked"
    />

    <div class="retailer-search-result-item-fi__mobile-show-more">
      <span
        class="retailer-search-result-item-fi__mobile-show-more__text"
        @click="toggleShowMore"
      >
        {{
          showMore
            ? $dict.get('dealer-Locator.Retailer-Show-Less')
            : $dict.get('dealer-Locator.Retailer-Show-More')
        }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AvailabilityIndicator, CloudImageFi, IconFi, SlideUpDownFi } from 'atoms';
import { computed, ref } from 'vue';

import ContactDetailFi from '@/components/retailer-search-fi/retailer-search-result-fi/ContactDetailFi.vue';
import { useBreakpoints } from '@/composables/breakpoint';
import type { ImageField } from '@/sitecoreFieldTypes';
import { getFaxHref, getMailToHref, getTelHref } from '@/utils/linkHelper';

defineOptions({ name: 'RetailerSearchResultItemFi' });

const props = withDefaults(
  defineProps<{
    data: { isStocked?: boolean | null };
    showStock?: boolean | null;
    partnerTypeLogo?: ImageField | null;
    theme?: string | null;
  }>(),
  {
    showStock: false,
    partnerTypeLogo: null,
    theme: null,
  },
);

const emit = defineEmits<{
  clicked: [payload: any];
}>();

const { isSM } = useBreakpoints();

const showMore = ref(false);

const retailer = computed(() => (props.data as any).Retailer);

const getTel = (tel: string) => getTelHref(tel);
const getFax = (fax: string) => getFaxHref(fax);
const getMailto = (mail: string) => getMailToHref(mail);

const toggleShowMore = () => {
  showMore.value = !showMore.value;
};
</script>

<style scoped src="./retailer-search-result-item-fi" />
